import React from 'react';
import { Card, HeaderCard } from '@ui-library/card';
import { LabelDiv, LabelMedium } from '@ui-library/typography';
import Button from '@ui-library/button';
import { Flex } from '@ui-library/misc';
import { SyncOutlined } from '@ant-design/icons';
import { SelectV1 } from '@ui-library/select';
import { ISourceStore } from '@stores/source';
import { Skeleton } from 'antd';
import { formatGraphLabel } from '@components/destinations/destination-view/events/utils';
import { observer } from 'mobx-react';
import theme from '@css/theme';
import useEvents, { SOURCE_TIME_FILTERS } from './hooks';
import classes from './events.module.scss';
import Graph from './graph';
import TrackEvents from './trackEvents';

const labelProps = { fontSize: '18px', lineHeight: '25px', color: theme.color.neutral1 };

const Events = ({ source }: { source: ISourceStore }) => {
  const {
    timeFilter,
    loading,
    setTimeFilter,
    trackFilters,
    page,
    onPageChange,
    onRefresh,
    eventStats,
    trackEvents,
    setTrackFilters,
    isTableLoading,
  } = useEvents(source);

  if (eventStats?.totalSent === undefined) {
    return <Skeleton active />;
  }

  const Header = (
    <Flex spaceBetween alignItemsCenter>
      <LabelDiv {...labelProps}>Event Ingestion</LabelDiv>
      <Flex>
        <Button
          secondary
          onClick={onRefresh}
          loading={loading}
          className={classes.button}
          disabled={isTableLoading}
        >
          <SyncOutlined />
          &nbsp;Refresh
        </Button>
        <SelectV1
          options={SOURCE_TIME_FILTERS}
          onChange={setTimeFilter}
          value={timeFilter}
          disabled={loading || isTableLoading}
        />
      </Flex>
    </Flex>
  );

  if (loading) {
    return (
      <>
        {Header}
        <Skeleton active />
      </>
    );
  }

  return (
    <>
      {Header}
      <Card className={classes.count_card}>
        <Flex flexDirection="column" justifyContentCenter className={classes.delivered}>
          <LabelDiv {...labelProps}>{eventStats.totalSent.toLocaleString()}</LabelDiv>
          <LabelMedium secondary fontWeight={400}>
            Ingested
          </LabelMedium>
        </Flex>
      </Card>
      {eventStats.totalSent > 0 && (
        <HeaderCard title="Event Flow" noContentPadding>
          <div className={classes.graph}>
            <Graph chartData={eventStats?.graph} labelFormat={formatGraphLabel(timeFilter)} />
          </div>
        </HeaderCard>
      )}
      <TrackEvents
        filters={trackFilters}
        events={trackEvents.data}
        setFilters={setTrackFilters}
        hasMoreEvents={trackEvents.hasMore}
        loading={isTableLoading}
        page={page}
        onPageChange={onPageChange}
      />
    </>
  );
};

export default observer(Events);
